import { addDays, differenceInDays } from 'date-fns';
import { GetCountsProps, PricingPlanCount } from 'contexts/accounts/types';
import { getPricingPlanIdentity } from 'contexts/pricingPlans/identity';
import { formatDateInUtc } from 'utils/helpers';
import { MAX_LICENSES_ACCOUNT } from 'constants/licenses';

interface AllLicencesProps {
  from: string;
  to: string;
  unlimited: string;
}

interface AllPricingProps {
  plan: string[];
}

export const getRangeLicenses = ({ from, to }: { from: string; to: string }): [number, number] => {
  const fromValue = parseInt(from);
  const toValue = parseInt(to);

  if (toValue === 0 && fromValue === 0) {
    return [0, MAX_LICENSES_ACCOUNT];
  }
  if (toValue === 0) {
    return [fromValue, MAX_LICENSES_ACCOUNT];
  } else {
    return [fromValue, toValue];
  }
};

export const isAllLicenses = ({ from, to, unlimited }: AllLicencesProps): boolean => {
  return from === '0' && to === '0' && unlimited !== 'true';
};

export const isAllPricing = ({ plan }: AllPricingProps): boolean => {
  return plan[0] === '' || plan.length === 0;
};

export const getPricingCount = (plan: PricingPlanCount, counts: GetCountsProps): number => {
  const pricingPlanCounts = counts?.pricing_plan_count || [];
  const hasStableIdentity = plan.code !== undefined || plan.legacy !== undefined;

  if (hasStableIdentity) {
    const identity = getPricingPlanIdentity(plan);
    const count = pricingPlanCounts.find(
      (item) =>
        (item.code !== undefined || item.legacy !== undefined) &&
        getPricingPlanIdentity(item) === identity
    )?.count;
    if (count !== undefined) return count;

    if (pricingPlanCounts.every((item) => item.code === undefined && item.legacy === undefined)) {
      return pricingPlanCounts.find((item) => item.name === plan.name)?.count || 0;
    }

    return 0;
  }

  // Compatibility for the existing name-only count payload. This path is
  // deliberately unavailable once stable identity metadata is supplied.
  return (
    pricingPlanCounts.find((item) => item.code === undefined && item.name === plan.name)?.count || 0
  );
};

/**
 * Resolves a count for a plan whose code is its complete card semantic. This
 * deliberately leaves getPricingCount's code + legacy identity matching intact
 * for callers that distinguish variants sharing a code or display name.
 */
export const getPricingCountByCode = (
  plan: Pick<PricingPlanCount, 'name' | 'code'> & { code: string },
  counts: GetCountsProps
): number => {
  const pricingPlanCounts = counts?.pricing_plan_count || [];
  const count = pricingPlanCounts.find((item) => item.code === plan.code)?.count;
  if (count !== undefined) return count;

  if (pricingPlanCounts.every((item) => item.code === undefined && item.legacy === undefined)) {
    return pricingPlanCounts.find((item) => item.name === plan.name)?.count || 0;
  }

  return 0;
};

/**
 * Returns the combined count for plans that intentionally share a customer-facing
 * label. Old name-only payloads are a single aggregate already, so they must not
 * be counted once per stable identity.
 */
export const getPricingCounts = (plans: PricingPlanCount[], counts: GetCountsProps): number => {
  const pricingPlanCounts = counts?.pricing_plan_count || [];
  const hasStableIdentity = pricingPlanCounts.some(
    (item) => item.code !== undefined || item.legacy !== undefined
  );

  if (!hasStableIdentity) {
    return pricingPlanCounts.find((item) => item.name === plans[0]?.name)?.count || 0;
  }

  return plans.reduce((total, plan) => total + getPricingCount(plan, counts), 0);
};

export interface PricingPlanFilterOption extends PricingPlanCount {
  filterValue: string;
}

const fallbackPricingPlanOptions: PricingPlanFilterOption[] = [
  {
    name: 'Professional',
    code: 'professional',
    legacy: null,
    count: 0,
    filterValue: 'professional',
  },
  { name: 'Enterprise', code: 'enterprise', legacy: null, count: 0, filterValue: 'enterprise' },
];

/**
 * Converts count payloads into filter rows without ever deduplicating by
 * customer-facing name. Name-only payloads retain the pre-existing rows.
 * A legacy plan variant with zero accounts is dropped entirely (not shown
 * as a filter option or a stats card); current (non-legacy) variants still
 * show at zero.
 */
export const getPricingPlanFilterOptions = (counts: GetCountsProps): PricingPlanFilterOption[] => {
  const pricingPlanCounts = counts?.pricing_plan_count || [];
  const hasPlanCodes = pricingPlanCounts.some((item) => typeof item.code === 'string');

  if (!hasPlanCodes) return fallbackPricingPlanOptions;

  return pricingPlanCounts
    .filter((item) => typeof item.code === 'string')
    .filter((item) => !(item.legacy === true && item.count === 0))
    .map((item) => ({ ...item, filterValue: item.code }));
};

export const getAccountStatusCount = (type: string, counts: GetCountsProps): number => {
  if (counts?.status_count[type]) return counts.status_count[type];

  return 0;
};

export const isCloseToExpiration = (from: string, to: string): boolean => {
  return differenceInDays(new Date(to), new Date(from)) === 30;
};

export const generateCloseToExpirationValues = (): [string, string] => {
  const from = formatDateInUtc(new Date(), 'yyyy-MM-dd');
  const to = formatDateInUtc(addDays(new Date(from), 30), 'yyyy-MM-dd');
  return [from, to];
};
